/* Interaction-dismissed visual hint; never changes Swiper's active slide or video state. */
(function () {
  'use strict';
  const learnedKey = 'piano-gallery-peek-interacted-at';
  const cooldown = 24 * 60 * 60 * 1000;
  function allowed() {
    const value = localStorage.getItem(learnedKey);
    const timestamp = Number(value);
    return value === null || !Number.isFinite(timestamp) ||
      Date.now() < timestamp || Date.now() - timestamp >= cooldown;
  }
  window.pianoGalleryPeek = function (swiper) {
    const mobile = window.matchMedia('(max-width: 767px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mobile.matches || reduced.matches || swiper.slides.length < 2 ||
        !swiper.wrapperEl.animate || swiper.realIndex !== 0) return;
    // If storage is unavailable, skip rather than repeat the hint on every product.
    try { if (!allowed()) return; } catch (_) { return; }
    const first = swiper.slides[swiper.activeIndex]?.querySelector('img');
    const next = swiper.slides[swiper.activeIndex + 1]?.querySelector('img');
    if (!first || !next) return;
    let ended = false, timer, deadline, animation;
    const cleanups = [];
    function listen(target, event, handler) {
      target.addEventListener(event, handler, { passive: true });
      cleanups.push(() => target.removeEventListener(event, handler));
    }
    function stop() {
      ended = true;
      clearTimeout(timer);
      clearTimeout(deadline);
      if (animation) animation.cancel();
      cleanups.forEach(cleanup => cleanup());
    }
    function interact() {
      try { localStorage.setItem(learnedKey, String(Date.now())); } catch (_) { /* Storage may be revoked. */ }
      stop();
    }
    let gestureStart = null;
    function point(event) {
      const touch = event.touches?.[0] || event.changedTouches?.[0];
      const x = Number(touch?.clientX ?? event.clientX);
      const y = Number(touch?.clientY ?? event.clientY);
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
    }
    function startGesture(event) { gestureStart = point(event); }
    function moveGesture(event) {
      if (!gestureStart) return;
      const current = point(event);
      if (!current) return;
      const dx = current.x - gestureStart.x;
      const dy = current.y - gestureStart.y;
      if (Math.abs(dx) >= 8 && Math.abs(dx) > Math.abs(dy)) {
        gestureStart = null;
        interact();
      } else if (Math.abs(dy) >= 8 && Math.abs(dy) > Math.abs(dx)) {
        // A vertical gesture belongs to page scrolling and must not cancel the hint.
        gestureStart = null;
      }
    }
    function endGesture() { gestureStart = null; }
    function eligible() {
      const rect = swiper.el.getBoundingClientRect();
      const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
      return !ended && !swiper.destroyed && mobile.matches && !reduced.matches &&
        document.visibilityState === 'visible' && swiper.realIndex === 0 &&
        !swiper.animating && !(swiper.zoom?.scale > 1) && rect.width > 0 &&
        visible >= Math.min(rect.height, window.innerHeight) * 0.6;
    }
    function ready() {
      if (ended || timer || !first.complete || !first.naturalWidth ||
          !next.complete || !next.naturalWidth) return;
      timer = setTimeout(() => {
        if (!eligible()) { stop(); return; }
        try {
          if (!allowed()) { stop(); return; }
        } catch (_) { stop(); return; }
        const base = getComputedStyle(swiper.wrapperEl).transform;
        const origin = base === 'none' ? '' : base;
        clearTimeout(deadline);
        const reveal = Math.min(100, swiper.el.getBoundingClientRect().width * 0.25);
        const distance = swiper.rtlTranslate ? reveal : -reveal;
        // 500ms reveal, 700ms stationary hold, 600ms return. Segment easing
        // keeps the hold literal instead of distorting its timing globally.
        animation = swiper.wrapperEl.animate([
          { transform: `${origin} translateX(0px)`, offset: 0, easing: 'ease-in-out' },
          { transform: `${origin} translateX(${distance}px)`, offset: 500 / 1800, easing: 'linear' },
          { transform: `${origin} translateX(${distance}px)`, offset: 1200 / 1800, easing: 'ease-in-out' },
          { transform: `${origin} translateX(0px)`, offset: 1 }
        ], { duration: 1800, easing: 'linear' });
        animation.onfinish = () => { animation = null; }; // Keep listening for a later gallery gesture.
      }, 1200);
    }
    // Cancel only after a horizontal drag starts on the gallery. Vertical page
    // scrolling and interactions elsewhere leave the hint running.
    listen(swiper.el, 'pointerdown', startGesture);
    listen(swiper.el, 'touchstart', startGesture);
    for (const event of ['pointermove', 'touchmove']) listen(window, event, moveGesture);
    for (const event of ['pointerup', 'pointercancel', 'touchend', 'touchcancel']) listen(window, event, endGesture);
    listen(window, 'resize', stop);
    listen(window, 'pagehide', stop);
    listen(document, 'visibilitychange', stop);
    listen(reduced, 'change', stop);
    for (const img of [first, next]) {
      listen(img, 'load', ready);
      listen(img, 'error', stop);
    }
    swiper.on('beforeDestroy', stop);
    swiper.on('slideChange', stop);
    cleanups.push(() => {
      swiper.off('beforeDestroy', stop);
      swiper.off('slideChange', stop);
    });
    // Never force-download an adjacent photo just to run an animation.
    deadline = setTimeout(stop, 15000);
    ready();
  };
})();
